import {useEffect} from 'react';
import {v4 as uuidv4} from 'uuid';

import {safeLocalStorage} from '../../util/localStorage';

export const WB_AGENT_CROSS_TAB_SYNC_KEY = 'wb-agent-chat-sync';
const MAX_EVENT_AGE_MS = 5 * 60 * 1000;

const tabId = uuidv4();

type CrossTabEventBase = {
  threadId: string;
  timestamp: number;
  sourceTabId: string;
  nonce: string;
};

export type WBAgentCrossTabEvent =
  | (CrossTabEventBase & {
      type: 'turn-cancel-requested';
      turnId: string;
    })
  | (CrossTabEventBase & {
      type: 'turn-created';
      turnId?: string;
    })
  | (CrossTabEventBase & {
      type: 'turn-updated';
      turnId: string;
    })
  | (CrossTabEventBase & {
      type: 'turn-finished';
      outcome: 'idle' | 'error' | 'cancelled';
    })
  | (CrossTabEventBase & {
      type: 'thread-pin-changed';
    })
  | (CrossTabEventBase & {
      type: 'thread-renamed';
    })
  | (CrossTabEventBase & {
      type: 'thread-deleted';
    });

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

type PublishableEvent = DistributiveOmit<
  WBAgentCrossTabEvent,
  'sourceTabId' | 'timestamp' | 'nonce'
>;

type CrossTabChannel = Pick<
  BroadcastChannel,
  'postMessage' | 'addEventListener' | 'removeEventListener' | 'close'
>;

type CrossTabChannelFactory = (name: string) => CrossTabChannel | null;

const createDefaultChannel: CrossTabChannelFactory = name => {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }
  return new BroadcastChannel(name);
};

let createChannel = createDefaultChannel;
let publishChannel: CrossTabChannel | null | undefined;
const currentTabListeners = new Set<(event: WBAgentCrossTabEvent) => void>();

export function publishWBAgentCrossTabEvent(event: PublishableEvent): void {
  publishPayloadAcrossTabs(createEventPayload(event));
}

/**
 * Announces an externally-created thread turn to Chat in this tab and other
 * tabs. Chat-owned turns already update the current store through their live
 * runtime, so they should continue using publishWBAgentCrossTabEvent.
 */
export function publishExternalWBAgentTurnCreated(
  event: Extract<PublishableEvent, {type: 'turn-created'}>
): void {
  const payload = createEventPayload(event);
  currentTabListeners.forEach(listener => {
    try {
      listener(payload);
    } catch {
      // One in-tab consumer must not prevent delivery to other consumers or
      // the cross-tab transport.
    }
  });
  publishPayloadAcrossTabs(payload);
}

function publishPayloadAcrossTabs(payload: WBAgentCrossTabEvent): void {
  const channel = getPublishChannel();
  if (channel) {
    channel.postMessage(payload);
    return;
  }

  safeLocalStorage.setItem(
    WB_AGENT_CROSS_TAB_SYNC_KEY,
    JSON.stringify(payload)
  );
}

function createEventPayload(event: PublishableEvent): WBAgentCrossTabEvent {
  return {
    ...event,
    sourceTabId: tabId,
    timestamp: Date.now(),
    nonce: uuidv4(),
  };
}

export function useWBAgentCrossTabEvents(
  onEvent: (event: WBAgentCrossTabEvent) => void
): void {
  useEffect(() => subscribeWBAgentCrossTabEvents(onEvent), [onEvent]);
}

export function subscribeWBAgentCrossTabEvents(
  onEvent: (event: WBAgentCrossTabEvent) => void
): () => void {
  currentTabListeners.add(onEvent);
  const channel = createChannel(WB_AGENT_CROSS_TAB_SYNC_KEY);
  if (channel) {
    const handleMessage = (event: MessageEvent<unknown>) => {
      const parsed = parseWBAgentCrossTabEvent(event.data);
      if (parsed) {
        onEvent(parsed);
      }
    };
    channel.addEventListener('message', handleMessage);
    return () => {
      currentTabListeners.delete(onEvent);
      channel.removeEventListener('message', handleMessage);
      channel.close();
    };
  }

  const handleStorageChange = (event: StorageEvent) => {
    if (event.key !== WB_AGENT_CROSS_TAB_SYNC_KEY) {
      return;
    }
    const parsed = parseWBAgentCrossTabEvent(event.newValue);
    if (parsed) {
      onEvent(parsed);
    }
  };
  window.addEventListener('storage', handleStorageChange);
  return () => {
    currentTabListeners.delete(onEvent);
    window.removeEventListener('storage', handleStorageChange);
  };
}

export function parseWBAgentCrossTabEvent(
  value: unknown
): WBAgentCrossTabEvent | null {
  if (!value) {
    return null;
  }

  try {
    const parsed = (
      typeof value === 'string' ? JSON.parse(value) : value
    ) as Partial<WBAgentCrossTabEvent> & {rootTurnId?: string};
    const threadId = parsed.threadId ?? parsed.rootTurnId;
    if (
      parsed.sourceTabId === tabId ||
      typeof parsed.sourceTabId !== 'string' ||
      typeof threadId !== 'string' ||
      typeof parsed.timestamp !== 'number' ||
      typeof parsed.nonce !== 'string' ||
      Date.now() - parsed.timestamp > MAX_EVENT_AGE_MS
    ) {
      return null;
    }

    if (
      parsed.type !== 'turn-cancel-requested' &&
      parsed.type !== 'turn-created' &&
      parsed.type !== 'turn-updated' &&
      parsed.type !== 'turn-finished' &&
      parsed.type !== 'thread-pin-changed' &&
      parsed.type !== 'thread-renamed' &&
      parsed.type !== 'thread-deleted'
    ) {
      return null;
    }
    if (
      (parsed.type === 'turn-cancel-requested' ||
        parsed.type === 'turn-updated') &&
      typeof parsed.turnId !== 'string'
    ) {
      return null;
    }

    if (
      parsed.type === 'turn-finished' &&
      parsed.outcome !== 'idle' &&
      parsed.outcome !== 'error' &&
      parsed.outcome !== 'cancelled'
    ) {
      return null;
    }

    const {rootTurnId: _legacyRootTurnId, ...rest} = parsed;
    return {...rest, threadId} as WBAgentCrossTabEvent;
  } catch {
    return null;
  }
}

function getPublishChannel(): CrossTabChannel | null {
  if (publishChannel === undefined) {
    publishChannel = createChannel(WB_AGENT_CROSS_TAB_SYNC_KEY);
  }
  return publishChannel;
}

export function __setWBAgentCrossTabChannelFactoryForTests(
  factory: CrossTabChannelFactory
): void {
  publishChannel?.close();
  publishChannel = undefined;
  createChannel = factory;
}

export function __resetWBAgentCrossTabSyncForTests(): void {
  publishChannel?.close();
  publishChannel = undefined;
  currentTabListeners.clear();
  createChannel = createDefaultChannel;
}
